import React, { useState } from 'react';
import { Search, ChevronDown, ChevronUp, Lock } from 'lucide-react';
import { AVP } from '../types';
import { formatCurrency, formatNumber } from '../utils/formatters';
interface PerformanceTableProps {
  avps: AVP[];
}
type SortField = 'rank' | 'name' | 'division' | 'loanVolume' | 'totalRevenue' | 'loansClosed';
export function PerformanceTable({
  avps
}: PerformanceTableProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<SortField>('rank');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  const filteredData = avps.filter(avp => avp.name.toLowerCase().includes(searchTerm.toLowerCase()) || avp.division.toLowerCase().includes(searchTerm.toLowerCase()));
  const sortedData = [...filteredData].sort((a, b) => {
    const aValue = a[sortField];
    const bValue = b[sortField];
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortDirection === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    }
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    }
    return 0;
  });
  const SortIcon = ({
    field
  }: {
    field: SortField;
  }) => {
    if (sortField !== field) return <ChevronDown className="w-4 h-4 opacity-0 group-hover:opacity-50 transition-opacity" data-id="element-260" />;
    return sortDirection === 'asc' ? <ChevronUp className="w-4 h-4 text-indigo-400" data-id="element-261" /> : <ChevronDown className="w-4 h-4 text-indigo-400" data-id="element-262" />;
  };
  return <section className="mb-12" data-id="element-263">
      <div className="flex flex-col md:flex-row justify-between items-end md:items-center mb-6 gap-4" data-id="element-264">
        <h2 className="text-xl font-semibold text-white" data-id="element-265">
          Detailed Performance
        </h2>

        <div className="relative w-full md:w-64" data-id="element-266">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-267" />
          <input type="text" placeholder="Search leaders or divisions..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-xl py-2 pl-10 pr-4 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 transition-all" data-id="element-268" />
        </div>
      </div>

      <div className="glass-panel rounded-3xl overflow-hidden" data-id="element-269">
        <div className="overflow-x-auto" data-id="element-270">
          <table className="w-full text-left border-collapse" data-id="element-271">
            <thead data-id="element-272">
              <tr className="border-b border-white/10 bg-white/[0.02]" data-id="element-273">
                <th className="py-4 px-6 text-xs font-semibold text-slate-400 uppercase tracking-wider cursor-pointer group" onClick={() => handleSort('rank')} data-id="element-274">
                  <div className="flex items-center gap-1" data-id="element-275">
                    Rank <SortIcon field="rank" data-id="element-276" />
                  </div>
                </th>
                <th className="py-4 px-6 text-xs font-semibold text-slate-400 uppercase tracking-wider cursor-pointer group" onClick={() => handleSort('name')} data-id="element-277">
                  <div className="flex items-center gap-1" data-id="element-278">
                    Leader <SortIcon field="name" data-id="element-279" />
                  </div>
                </th>
                <th className="py-4 px-6 text-xs font-semibold text-slate-400 uppercase tracking-wider cursor-pointer group" onClick={() => handleSort('division')} data-id="element-280">
                  <div className="flex items-center gap-1" data-id="element-281">
                    Division <SortIcon field="division" data-id="element-282" />
                  </div>
                </th>
                <th className="py-4 px-6 text-xs font-semibold text-slate-400 uppercase tracking-wider cursor-pointer group" onClick={() => handleSort('loanVolume')} data-id="element-283">
                  <div className="flex items-center gap-1 justify-end" data-id="element-284">
                    Volume <SortIcon field="loanVolume" data-id="element-285" />
                  </div>
                </th>
                <th className="py-4 px-6 text-xs font-semibold text-slate-400 uppercase tracking-wider cursor-pointer group" onClick={() => handleSort('totalRevenue')} data-id="element-286">
                  <div className="flex items-center gap-1 justify-end" data-id="element-287">
                    Revenue <SortIcon field="totalRevenue" data-id="element-288" />
                  </div>
                </th>
                <th className="py-4 px-6 text-xs font-semibold text-slate-400 uppercase tracking-wider cursor-pointer group" onClick={() => handleSort('loansClosed')} data-id="element-289">
                  <div className="flex items-center gap-1 justify-end" data-id="element-290">
                    Loans <SortIcon field="loansClosed" data-id="element-291" />
                  </div>
                </th>
                <th className="py-4 px-6 text-xs font-semibold text-slate-400 uppercase tracking-wider" data-id="element-292">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5" data-id="element-293">
              {sortedData.map(avp => <tr key={avp.id} className="hover:bg-white/[0.02] transition-colors group" data-id="element-294">
                  <td className="py-4 px-6" data-id="element-295">
                    <div className="flex items-center gap-3" data-id="element-296">
                      <span className={`w-6 text-center font-semibold ${avp.rank <= 3 ? 'text-yellow-400' : 'text-slate-300'}`} data-id="element-297">
                        {avp.rank}
                      </span>
                    </div>
                  </td>
                  <td className="py-4 px-6" data-id="element-298">
                    <div className="flex items-center gap-3" data-id="element-299">
                      <div className="w-8 h-8 rounded-full bg-indigo-500/20 border border-indigo-500/30 flex items-center justify-center text-indigo-300 text-xs font-semibold" data-id="element-300">
                        {avp.initials}
                      </div>
                      <div data-id="element-301">
                        <p className="text-sm font-medium text-white" data-id="element-302">
                          {avp.name}
                        </p>
                        <p className="text-xs text-slate-500" data-id="element-303">
                          {avp.loCount} LOs
                        </p>
                      </div>
                    </div>
                  </td>
                  <td className="py-4 px-6 text-sm text-slate-300" data-id="element-304">
                    {avp.division}
                  </td>
                  <td className="py-4 px-6 text-sm text-white font-medium text-right" data-id="element-305">
                    {formatCurrency(avp.loanVolume, true)}
                  </td>
                  <td className="py-4 px-6 text-sm text-emerald-400 font-medium text-right" data-id="element-306">
                    {formatCurrency(avp.totalRevenue, true)}
                  </td>
                  <td className="py-4 px-6 text-sm text-slate-300 text-right" data-id="element-307">
                    {formatNumber(avp.loansClosed)}
                  </td>
                  <td className="py-4 px-6" data-id="element-308">
                    <div className="flex items-center gap-2" data-id="element-309">
                      {avp.snapshotStatus === 'Finalized Snapshot' ? <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-500/10 border border-slate-500/20 text-slate-400 text-xs font-medium" data-id="element-310">
                          <Lock className="w-3 h-3" data-id="element-311" /> Locked
                        </span> : <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-indigo-500/10 border border-indigo-500/20 text-indigo-300 text-xs font-medium" data-id="element-312">
                          <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 animate-pulse" data-id="element-313" />{' '}
                          Active
                        </span>}
                    </div>
                  </td>
                </tr>)}
              {sortedData.length === 0 && <tr data-id="element-314">
                  <td colSpan={7} className="py-12 text-center text-slate-500 text-sm" data-id="element-315">
                    No leaders found matching your search.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </div>
    </section>;
}